import { comonDataApi } from '@/api/config-setup'
import areaApi from '@/api/config/areaApi'
import emitterDataConfigApi from '@/api/config/emitterDataConfig'
import etReferenceDataConfig from '@/api/config/etReferenceDataConfig'
import hydraulicDataConfig from '@/api/config/hydraulicDataConfig'
import plantDataConfigApi from '@/api/config/plantDataConfigApi'
import soilDataConfigApi from '@/api/config/soilDataConfigApi'
import stationDataConfigApi from '@/api/config/stationDataConfig'
import InputFile from '@/components/form-control/InputFile'
import { CopyOneIcon, DatabaseAlerIcon } from '@/components/icons'
import { XLSX_ACEPT, XLSX_FORMAT } from '@/constant/common'
import { CONFIG_SETUP_ROUTES, OPERATIONS_ROUTES } from '@/constant/route'
import useCheckOverlay from '@/features/admin/hooks/locationInterface/useCheckOverlay'
import { useAuthContext } from '@/features/auth/context'
import SetUpHeaderModal from '@/features/design-system/components/common/SetUpHeaderModal'
import SetUpLayout from '@/features/design-system/components/common/SetUpLayout'
import MapViewRightMenu from '@/features/operations/components/pages/MapView/MapViewRightMenu'
import { NotifyService } from '@/helpers'
import { SearchStation } from '@/types'
import useSearchInterface from '@features/admin/hooks/interface/useSearchInterface'
import useSearchInterfaceId from '@features/admin/hooks/interface/useSearchInterfaceId'
import useValidateCurrentInterface from '@features/design-system/hooks/setup/useValidateCurrentInterface'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { saveAs } from 'file-saver'
import { useAppDispatch } from '@/store/stores'
import { updateInterfaceStatus } from '@/store/slices/interfaceByOrganization/interfaceByOrganizationSlice'
import { memo, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import { PinStationsOnMap } from '../SetUpThird/components'
import CommonDataTableV2 from '../SetUpSecond/CommonDataTableV2'
import classes from './SetUpSecondV2.styles.module.scss'
import { UploadProgressV2 } from './components/UploadProgressV2'
import { useCommonDataUploadV2 } from '@/features/design-system/hooks/setup/useCommonDataUploadV2'

interface Props {}

enum STEP {
  CHOOSE_FILE = 0,
  UPLOADING,
  UPLOAD_ERROR,
  REVIEW,
  PIN_STATION,
}

// Helper to normalize API response to pagination format
// Some APIs return { data: [...], totalCount } while others return array directly
const toPaginationFormat = (response: any) => {
  if (!response) return null
  // Already has pagination format
  if (response.data && Array.isArray(response.data)) {
    return response
  }
  // Response is an array directly
  if (Array.isArray(response)) {
    return { data: response, totalCount: response.length }
  }
  // Other format - wrap it
  return { data: [], totalCount: 0 }
}

export const SetUpSecondV2 = memo((props: Props) => {
  // SCRUM-976: the URL :id is the single source of truth for the Common Data setup page.
  // The globally-selected interface (AuthContext, backed by localStorage) must NOT drive any
  // interface-scoped action here - another tab can change it via the cross-tab storage sync,
  // or it can be stale after a refresh/bookmark. That previously made the upload target a
  // different interface than the one the page (and the reset step, which already uses :id) was
  // showing. We keep a read of the global value only to surface a mismatch warning below.
  const { interfaceId: globalInterfaceId } = useAuthContext()
  const { id } = useParams()
  const interfaceId = Number(id)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  // Step state
  const [step, setStep] = useState(STEP.CHOOSE_FILE)

  // Reset data state
  const [isDeleted, setIsDeleted] = useState(false)
  const [loadingReset, isLoadingReset] = useState(false)
  const [isSetup, setIsSetup] = useState(false)

  // Download template state
  const [isDownloading, setIsDownloading] = useState(false)

  // Cancel confirmation dialog state
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)

  // Review data state - fetched from multiple APIs after import completes
  const [reviewData, setReviewData] = useState<{
    hydraulic: any
    etReference: any
    plant: any
    emitter: any
    areaZone: any
    soil: any
    station: any
  } | null>(null)
  const [isLoadingReview, setIsLoadingReview] = useState(false)
  const [param] = useState<SearchStation>({
    PageSize: 0,
    interfaceId: Number(interfaceId),
  })

  // Interface validation
  const { data: allInterfaces } = useSearchInterface({ PageSize: 0 })
  const { refetch: refetchInterfaceById } = useSearchInterfaceId(id)
  const { selectedInterface } = useValidateCurrentInterface({
    currentInterfaceId: id,
    allInterfaces,
    redirectTo: OPERATIONS_ROUTES.index.to,
    autoRedirect: false, // We handle redirect manually below
  })

  // Redirect if interface is already setup (status === true)
  // BUT only when first entering the page (CHOOSE_FILE step)
  // Don't redirect when user is actively in UPLOADING/REVIEW flow
  useEffect(() => {
    if (
      selectedInterface &&
      selectedInterface.status === true &&
      step === STEP.CHOOSE_FILE
    ) {
      navigate(OPERATIONS_ROUTES.index.to)
    }
  }, [selectedInterface, step, navigate])

  // Upload hook V2
  const uploadHook = useCommonDataUploadV2({
    interfaceId: Number(interfaceId),
    onSuccess: () => {
      setStep(STEP.REVIEW)
    },
    onError: (error) => {
      setStep(STEP.UPLOAD_ERROR)
      NotifyService.error(error)
    },
    onCancel: async () => {
      // After cancel completes and no active task, check interface status
      const { data: interfaceData } = await refetchInterfaceById()

      if (interfaceData?.status === true) {
        // Sync Redux store before navigating to prevent redirect loop
        dispatch(updateInterfaceStatus({ id: Number(id), status: true }))
        navigate(OPERATIONS_ROUTES.index.to)
      } else {
        // Interface not setup, require Reset Data before upload
        setIsDeleted(false)
        setStep(STEP.CHOOSE_FILE)
      }
      toast.success('Import cancelled')
    },
  })

  // Cancel handlers
  const handleCancelClick = () => {
    setShowCancelConfirm(true)
  }

  const handleConfirmCancel = async () => {
    setShowCancelConfirm(false)
    await uploadHook.cancelTask()
  }

  const handleCloseCancelConfirm = () => {
    setShowCancelConfirm(false)
  }

  // Check for active task on mount
  useEffect(() => {
    const checkForActiveTask = async () => {
      const activeTask = await uploadHook.checkActiveTask()
      if (activeTask?.hasActiveTask) {
        setStep(STEP.UPLOADING)
      }
    }

    checkForActiveTask()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch all review data from multiple APIs when entering review step
  useEffect(() => {
    const fetchAllReviewData = async () => {
      setIsLoadingReview(true)
      try {
        const searchParams = { interfaceId: Number(interfaceId), PageSize: 0 }

        // Fetch all data in parallel
        const [
          hydraulicRes,
          etReferenceRes,
          plantRes,
          emitterRes,
          areaRes,
          soilRes,
          stationRes,
        ] = await Promise.all([
          hydraulicDataConfig.getAll(searchParams).catch(() => ({ data: null })),
          etReferenceDataConfig.getAll(searchParams).catch(() => ({ data: null })),
          plantDataConfigApi.getAll(searchParams).catch(() => ({ data: null })),
          emitterDataConfigApi.getAll(searchParams).catch(() => ({ data: null })),
          areaApi.getAll(searchParams).catch(() => ({ data: null })),
          soilDataConfigApi.getAll(searchParams).catch(() => ({ data: null })),
          stationDataConfigApi.getListWithDetail(Number(interfaceId), param).catch(() => ({ data: null })),
        ])

        setReviewData({
          hydraulic: toPaginationFormat(hydraulicRes?.data),
          etReference: toPaginationFormat(etReferenceRes?.data),
          plant: toPaginationFormat(plantRes?.data),
          emitter: toPaginationFormat(emitterRes?.data),
          areaZone: areaRes?.data, // Area has special format { data: [...], totalCount }
          soil: toPaginationFormat(soilRes?.data),
          station: stationRes?.data,
        })
      } catch (error) {
        console.error('Failed to fetch review data:', error)
      } finally {
        setIsLoadingReview(false)
      }
    }

    if (step === STEP.REVIEW) {
      fetchAllReviewData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, interfaceId])

  // Navigate when upload completes
  useEffect(() => {
    if (uploadHook.isCompleted) {
      setStep(STEP.REVIEW)
    }
  }, [uploadHook.isCompleted])

  // Handle error state
  useEffect(() => {
    if (uploadHook.isFailed) {
      setStep(STEP.UPLOAD_ERROR)
    }
  }, [uploadHook.isFailed])

  // SCRUM-976 safety net: surface (do not block) when the globally-selected interface differs
  // from the one being set up. The page already targets the URL :id for every action, so the
  // upload is safe regardless - this only flags the divergence for observability (Seq/console)
  // and warns the user. We intentionally do NOT hard-block: a mismatch is legitimate when the
  // page is reached via refresh/bookmark, and blocking would prevent a valid setup. We also do
  // NOT auto-sync the global value here, to avoid disrupting another tab via the storage event.
  useEffect(() => {
    if (
      globalInterfaceId != null &&
      !Number.isNaN(interfaceId) &&
      Number(globalInterfaceId) !== interfaceId
    ) {
      console.warn(
        `[SetUpSecondV2] Selected-interface mismatch (SCRUM-976): global=${globalInterfaceId} url=${interfaceId}. Using the URL interface for setup/upload.`
      )
      toast.error(
        'Heads up: your selected controller differs from the one open here. This page will set up the controller shown in the address bar.'
      )
    }
  }, [globalInterfaceId, interfaceId])

  const { data: errorOverlay } = useCheckOverlay(interfaceId)

  const onBack = () => {
    navigate(CONFIG_SETUP_ROUTES.setupFirst.to(id || ''))
  }

  const onErrors = () => {}

  const onDownload = async () => {
    setIsDownloading(true)
    try {
      const res = await comonDataApi.getCommonTemplate(Number(interfaceId))
      saveAs(res.data, 'CommonData_Template.xlsx')
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        toast.error('Controller does not exist')
      } else {
        toast.error('Failed to download the template. Please try again later.')
      }
    } finally {
      setIsDownloading(false)
    }
  }

  const onClose = () => {
    navigate(OPERATIONS_ROUTES.index.to)
  }

  const onTryAgain = () => {
    setIsDeleted(false)
    setStep(STEP.CHOOSE_FILE)
  }

  /**
   * @description:
   * check carefully before delete data
   */
  const handleDelete = async () => {
    isLoadingReset(true)
    try {
      // Step 1: Send the initial request with type = 1 and page = 1
      let type = 1
      let page = 1

      let res = await comonDataApi.deleteCommonType(Number(id), type)

      if (res.data.success === true) {
        type = 2
        do {
          res = await comonDataApi.deleteCommonType(Number(id), type, page)
          page += 1
        } while (res.data.isPaging === true)

        // Step 3: Send the final request with type = 3
        type = 3
        res = await comonDataApi.deleteCommonType(Number(id), type)

        if (res.data.success === true) {
          toast.success('Reset data successful')
          setIsDeleted(true)
          isLoadingReset(false)
        } else {
          toast.error(res.data.message)
          setIsDeleted(false)
        }
      } else {
        isLoadingReset(false)
        toast.error(res.data.message)
      }
    } catch (error) {
      isLoadingReset(false)
      toast.error('Delete failed. Please check your connection.')
    } finally {
      isLoadingReset(false)
      setIsSetup(false)
    }
  }

  const onChangeFile = async (file: File[]) => {
    if (!file?.length) return

    setStep(STEP.UPLOADING)
    await uploadHook.upload(file[0])
  }

  const handleNext = async () => {
    if (!!errorOverlay) {
      setStep(STEP.PIN_STATION)
    } else {
      // Poll interface status until true or timeout (max 60 seconds)
      const MAX_WAIT_TIME = 60000
      const POLL_INTERVAL = 2000
      const startTime = Date.now()

      while (Date.now() - startTime < MAX_WAIT_TIME) {
        const { data: interfaceData } = await refetchInterfaceById()

        if (interfaceData?.status === true) {
          // Sync Redux store before navigating to prevent infinite redirect loop
          // SelectLabelDashboard reads status from Redux (stale) while SetUpContainer
          // reads from React Query (fresh) - mismatch causes redirect loop
          dispatch(updateInterfaceStatus({ id: Number(id), status: true }))
          navigate(OPERATIONS_ROUTES.index.to)
          return
        }

        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
      }

      // Timeout reached
      toast.error('Interface setup timed out. Please try again.')
    }
  }

  const render = () => {
    switch (step) {
      case STEP.CHOOSE_FILE:
        return (
          <Box className={classes.root}>
            <Typography className={classes.pageTitle}>Common Data</Typography>
            <Stack alignItems="flex-start">
              <Stack direction="row" spacing={1}>
                <Typography className={classes.stepTitle}>
                  Step 1. Reset common data
                </Typography>
              </Stack>

              <Box className={`${classes.actions} ${classes.actionsDow}`}>
                <LoadingButton
                  variant="secondary"
                  className={classes.btnDownload}
                  onClick={() => {
                    if (!selectedInterface?.status) {
                      setIsSetup(true)
                    } else {
                      handleDelete()
                    }
                  }}
                  startIcon={<CopyOneIcon />}
                  loading={loadingReset}
                >
                  Reset data
                </LoadingButton>
              </Box>
              <Typography className={classes.stepTitle}>
                Step 2. Fill in your data in the Excel template
              </Typography>
              <Box className={`${classes.actions} ${classes.actionsDow}`}>
                {isDownloading ? (
                  <LoadingButton
                    className={classes.btnDownload}
                    startIcon={<CopyOneIcon />}
                    loading
                    variant="secondary"
                  >
                    Download Excel Template
                  </LoadingButton>
                ) : (
                  <Button
                    variant="secondary"
                    className={classes.btnDownload}
                    onClick={onDownload}
                    startIcon={<CopyOneIcon />}
                  >
                    Download Excel Template
                  </Button>
                )}
              </Box>
              <Typography className={classes.stepTitle}>
                Step 3. Upload the XLSX
              </Typography>
              <Button
                sx={{
                  ...(!isDeleted && {
                    opacity: 0.5,
                    pointerEvents: 'none',
                    userSelect: 'none',
                  }),
                }}
                variant="secondary"
              >
                <InputFile
                  accept={XLSX_ACEPT}
                  formats={XLSX_FORMAT}
                  onChange={onChangeFile}
                />
              </Button>
            </Stack>
          </Box>
        )

      case STEP.UPLOADING:
        return (
          <Box className={classes.root}>
            <Typography className={classes.pageTitle}>Common Data</Typography>
            <UploadProgressV2
              progress={uploadHook.overallProgress}
              currentStep={uploadHook.currentStepName}
              totalSteps={uploadHook.progress?.steps.length || 0}
              completedSteps={
                uploadHook.progress?.steps.filter((s) => s.status === 2).length || 0
              }
              isUploading={uploadHook.isUploading}
              isCancelling={uploadHook.isCancelling}
              onCancel={handleCancelClick}
            />
            <Typography className={classes.title}>Processing Data</Typography>
            <Typography className={classes.description}>
              Please do not close the window
            </Typography>
          </Box>
        )

      case STEP.UPLOAD_ERROR:
        return (
          <Box className={classes.root}>
            <Typography className={classes.pageTitle}>Common Data</Typography>
            <DatabaseAlerIcon className={classes.errorIcon} />
            <Typography className={classes.title}>
              Unable to import data
            </Typography>
            <Typography className={classes.description}>
              {uploadHook.error || 'The file is not in the right format, or has typo errors'}
            </Typography>
            <Box className={classes.actions}>
              <Button className={classes.btnTry} onClick={onTryAgain}>
                Try Again
              </Button>
            </Box>
          </Box>
        )

      case STEP.REVIEW:
        return (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              className={classes.Table}
            >
              <Typography className={classes.TableTitle}>
                Review Common Data
              </Typography>
              <Stack alignItems="center">
                <Button
                  variant="contained"
                  className={classes.btnTry}
                  onClick={() => handleNext()}
                  disabled={isLoadingReview}
                >
                  {isLoadingReview ? 'Loading...' : 'Next'}
                </Button>
              </Stack>
            </Stack>
            <CommonDataTableV2
              dataHydraulic={reviewData?.hydraulic}
              dataEt={reviewData?.etReference}
              dataPlant={reviewData?.plant}
              dataEmitter={reviewData?.emitter}
              dataAreas={reviewData?.areaZone?.data}
              dataSoil={reviewData?.soil}
              totalCountAreas={reviewData?.areaZone?.totalCount}
            />
          </>
        )

      case STEP.PIN_STATION:
        return (
          <PinStationsOnMap
            interfaceId={Number(interfaceId)}
            data={reviewData?.station}
            onClose={onClose}
          />
        )
    }
  }

  return (
    <SetUpLayout
      title="Common Data"
      rightLayout={
        step === STEP.PIN_STATION || !errorOverlay ? null : (
          <Stack className={classes.mapViewRight}>
            <MapViewRightMenu />
          </Stack>
        )
      }
      header={
        step === STEP.PIN_STATION ? null : (
          <SetUpHeaderModal
            title="Common Data"
            onCancel={step === STEP.UPLOADING ? onErrors : onBack}
            showBtnClose={!(step === STEP.REVIEW)}
          />
        )
      }
      fullWidth={step === STEP.PIN_STATION ? true : false}
    >
      {render()}

      <Dialog
        open={isSetup}
        onClose={() => {
          setIsSetup(false)
        }}
      >
        <Box
          sx={{
            backgroundColor: 'background.default',
          }}
        >
          <Stack sx={{ padding: '14px 30px' }}>
            <Typography
              sx={{
                color: 'text.main',
                fontWeight: 400,
                fontSize: '20px',
                lineHeight: '24px',
                minHeight: '24px',
                minWidth: '192px',
              }}
            >
              Warning
            </Typography>
          </Stack>

          <Stack
            sx={{
              backgroundColor: 'common.white',
              minWidth: '378px',
              padding: '30px',
              boxShadow: '0px 0px 30px rgba(132, 132, 132, 0.25)',
            }}
          >
            <Stack>
              <Typography
                sx={{
                  color: '#000000',
                  marginBottom: '10px',
                }}
                variant="h3"
              >
                You're resetting all data for the controller{' '}
                <b>
                  {selectedInterface?.interfaceCustomName} (
                  {selectedInterface?.interface_name})
                </b>
              </Typography>

              <Typography
                sx={{
                  fontSize: '14px',
                  color: '#FF0000',
                  marginBottom: '20px',
                }}
                variant="h3"
              >
                This action is not reversible, would you like to proceed?
              </Typography>
            </Stack>

            <Box
              sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}
            >
              <Button
                variant="secondary"
                className={classes.btnTry}
                onClick={() => setIsSetup(false)}
              >
                Close
              </Button>

              {loadingReset ? (
                <LoadingButton
                  className={classes.btnTry}
                  startIcon={<CopyOneIcon />}
                  loading
                  variant="contained"
                >
                  Confirm
                </LoadingButton>
              ) : (
                <Button
                  variant="contained"
                  className={classes.btnTry}
                  onClick={() => handleDelete()}
                >
                  Confirm
                </Button>
              )}
            </Box>
          </Stack>
        </Box>
      </Dialog>

      {/* Cancel import confirmation dialog */}
      <Dialog open={showCancelConfirm} onClose={handleCloseCancelConfirm}>
        <Box sx={{ backgroundColor: 'background.default' }}>
          <DialogTitle sx={{ padding: '41px 152px 4px 34px' }}>
            <Typography
              sx={{
                color: 'text.main',
                fontWeight: 400,
                fontSize: '20px',
                lineHeight: '24px',
                minHeight: '24px',
                minWidth: '192px',
              }}
            >
              Cancel Import?
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ paddingLeft: '34px', paddingBottom: '18px' }}>
            <Typography
              sx={{
                color: 'text.main',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '1.3',
                maxWidth: '314px',
              }}
            >
              Are you sure you want to cancel the import? This will stop the
              current import process.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ paddingBottom: '32px', paddingRight: '30px' }}>
            <Button
              variant="secondary"
              sx={{ width: '99px' }}
              onClick={handleCloseCancelConfirm}
            >
              No
            </Button>
            <Button
              variant="primary"
              sx={{ width: '99px' }}
              onClick={handleConfirmCancel}
            >
              Yes
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </SetUpLayout>
  )
})

SetUpSecondV2.displayName = 'SetUpSecondV2'

export default SetUpSecondV2
